import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import {
  BrandVoucher,
  BrandVoucherStatusEnum,
  brandVoucherStatusLabel,
} from '@/app/common/general-types/brand-voucher.dto';
import { VoucherService } from '@/app/services/voucher/voucher.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { getAdminSettingsToolPath } from '../../../admin-tool-registry/admin-tool.registry';

@Component({
  selector: 'admin-tool-brand-vouchers-list',
  imports: [CurrencyPipe, DatePipe],
  templateUrl: './brand-vouchers-list.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBrandVouchersList implements OnInit {
  private readonly voucherService = inject(VoucherService);
  private readonly toast = inject(ToastNotificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly vouchers = signal<BrandVoucher[]>([]);
  readonly searchQuery = signal('');
  readonly nameSort = signal<'asc' | 'desc'>('asc');
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly disablingId = signal<string | null>(null);

  readonly filteredVouchers = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const direction = this.nameSort() === 'asc' ? 1 : -1;
    return this.vouchers()
      .filter((voucher) => !query || voucher.name.toLowerCase().includes(query))
      .slice()
      .sort(
        (a, b) =>
          direction *
          a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
      );
  });

  ngOnInit(): void {
    this.load();
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  toggleNameSort(): void {
    this.nameSort.update((direction) => (direction === 'asc' ? 'desc' : 'asc'));
  }

  openVoucher(voucher: BrandVoucher): void {
    void this.router.navigate([getAdminSettingsToolPath('brand-vouchers'), 'list', voucher.id]);
  }

  onRowKeydown(event: KeyboardEvent, voucher: BrandVoucher): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openVoucher(voucher);
    }
  }

  async copyName(event: Event, name: string): Promise<void> {
    event.stopPropagation();
    const value = name?.trim();
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      await this.toast.alert({
        title: 'Copied',
        message: 'Copied',
        type: 'success',
        icon: 'success',
      });
    } catch {
      await this.toast.alert({
        title: 'Copy failed',
        message: 'Could not copy the voucher name.',
        type: 'error',
        icon: 'error',
      });
    }
  }

  statusLabel(status: BrandVoucherStatusEnum): string {
    return brandVoucherStatusLabel(status);
  }

  isActive(voucher: BrandVoucher): boolean {
    return voucher.status === BrandVoucherStatusEnum.Active;
  }

  terminalDate(voucher: BrandVoucher): string | Date | null {
    return voucher.status === BrandVoucherStatusEnum.Expired ? voucher.expiredAt : null;
  }

  disable(event: Event, voucher: BrandVoucher): void {
    event.stopPropagation();
    if (!this.isActive(voucher) || this.disablingId()) {
      return;
    }

    this.disablingId.set(voucher.id);
    this.voucherService
      .disable(voucher.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.disablingId.set(null);
          this.vouchers.update((list) => list.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
          this.toast.alert({
            title: 'Voucher disabled',
            message: `${updated.name} is now expired and can no longer be redeemed.`,
            type: 'success',
            icon: 'success',
          });
          this.cdr.detectChanges();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.disablingId.set(null);
          const message = err?.error?.message || err?.message || 'Could not disable the voucher.';
          this.toast.alert({
            title: 'Could not disable voucher',
            message: Array.isArray(message) ? message.join(', ') : String(message),
            type: 'error',
            icon: 'error',
          });
          this.cdr.detectChanges();
        },
      });
  }

  private load(): void {
    this.loadingState.set('loading');
    this.voucherService
      .listAll()
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of([] as BrandVoucher[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((vouchers) => {
        this.vouchers.set(vouchers);
        if (this.loadingState() !== 'error') {
          this.loadingState.set('loaded');
        }
        this.cdr.detectChanges();
      });
  }
}
